import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  FiltersService, 
  FilterResponse, 
  FilterGroup 
} from '../services/filtersService';

try {
  const testResult = FiltersService.testMappings();
} catch (error) {
  console.error('❌ Test failed:', error);
}

interface UseFiltersOptions {
  enabled?: boolean;
  cacheTime?: number;
}

interface UseFiltersReturn {
  filtersData: FilterGroup[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  getGroupById: (groupId: number) => FilterGroup | undefined;
  getOptionById: (groupId: number, optionId: number) => any;
  hasGroup: (groupId: number) => boolean;
}

const filtersCache = new Map<string, {
  data: FilterResponse;
  timestamp: number;
}>();

const CACHE_DURATION = 0; // Disable cache for immediate refresh - set to 0 for testing

export const useFilters = (
  actionId: number | null,
  categoryId: number | null,
  lang: string = 'th',
  options: UseFiltersOptions = {}
): UseFiltersReturn => {
  const { enabled: optionsEnabled = true, cacheTime = CACHE_DURATION } = options;

  const [filtersData, setFiltersData] = useState<FilterGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const cacheKey = useMemo(() => {
    if (!actionId || !categoryId) return null;
    return `filters:${actionId}:${categoryId}:${lang}`;
  }, [actionId, categoryId, lang]);

  const getCachedData = useCallback((key: string) => {
    const cached = filtersCache.get(key);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > cacheTime;
    if (isExpired) {
      filtersCache.delete(key);
      return null;
    }

    return cached.data;
  }, [cacheTime]);

  const setCachedData = useCallback((key: string, data: FilterResponse) => {
    filtersCache.set(key, {
      data,
      timestamp: Date.now()
    });
  }, []);

  const isEnabled = actionId !== null && categoryId !== null && optionsEnabled !== false;

  const fetchFilters = useCallback(async () => {
    if (!actionId || !categoryId || !cacheKey || !isEnabled) {
      return;
    }

    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      const cachedData = getCachedData(cacheKey);
      
      if (cachedData) {
        setFiltersData(cachedData.data.groups);
        setIsLoading(false);
        return;
      }

      const response = await FiltersService.getCompleteFilters(actionId, categoryId, lang);
      
      if (response.success && response.data.groups) {
        const sortedGroups = response.data.groups.sort((a, b) => a.order - b.order);
        
        sortedGroups.forEach(group => {
          group.options.sort((a, b) => a.order - b.order);
        });

        setFiltersData(sortedGroups);
        
        setCachedData(cacheKey, response);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('❌ [useFilters] API error:', err);
      const errorMessage = err instanceof Error ? err : new Error('Unknown error');
      setError(errorMessage);
      setIsError(true);
      setFiltersData([]);
    } finally {
      setIsLoading(false);
    }
  }, [actionId, categoryId, lang, cacheKey, isEnabled, getCachedData, setCachedData]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  const getGroupById = useCallback((groupId: number): FilterGroup | undefined => {
    return filtersData.find(group => group.group_id === groupId);
  }, [filtersData]);

  const getOptionById = useCallback((groupId: number, optionId: number) => {
    const group = getGroupById(groupId);
    return group?.options.find(option => option.id === optionId);
  }, [getGroupById]);

  const hasGroup = useCallback((groupId: number): boolean => {
    return filtersData.some(group => group.group_id === groupId);
  }, [filtersData]);

  const refetch = useCallback(async () => {
    if (cacheKey) {
      filtersCache.delete(cacheKey);
    }
    await fetchFilters();
  }, [cacheKey, fetchFilters]);

  return {
    filtersData,
    isLoading,
    isError,
    error,
    refetch,
    getGroupById,
    getOptionById,
    hasGroup
  };
};

export const useActionCategoryIds = (
  actionName: string | null,
  categoryName: string | null
) => {
  const actionId = useMemo(() => {
    const id = actionName ? FiltersService.mapActionToId(actionName) : null;
    return id;
  }, [actionName]);

  const categoryId = useMemo(() => {
    const id = categoryName ? FiltersService.mapCategoryToId(categoryName) : null;
    return id;
  }, [categoryName]);

  return { actionId, categoryId };
};

export const useFiltersWithNames = (
  actionName: string | null,
  categoryName: string | null,
  lang: string = 'th',
  options: UseFiltersOptions = {}
) => {
  const { actionId, categoryId } = useActionCategoryIds(actionName, categoryName);
  
  const prevActionNameRef = useRef<string | null>(null);
  const prevCategoryNameRef = useRef<string | null>(null);
  
  const result = useFilters(actionId, categoryId, lang, options);
  
  useEffect(() => {
    const hasActionChanged = prevActionNameRef.current !== actionName;
    const hasCategoryChanged = prevCategoryNameRef.current !== categoryName;
    
    if ((hasActionChanged || hasCategoryChanged) && actionId && categoryId) {
      const cacheKey = `filters:${actionId}:${categoryId}:${lang}`;
      filtersCache.delete(cacheKey);
      
      prevActionNameRef.current = actionName;
      prevCategoryNameRef.current = categoryName;
      
      if (result.refetch) {
        result.refetch();
      }
    } else {
      prevActionNameRef.current = actionName;
      prevCategoryNameRef.current = categoryName;
    }
  }, [actionName, categoryName, actionId, categoryId, lang, result.refetch]);
  
  const filters = useMemo(() => {
    if (!result.filtersData || result.filtersData.length === 0) {
      return [];
    }

    const filterNameMap: { [key: string]: string } = {
      'price': 'price',
      'ราคา': 'price',
      'price_range': 'price',
      'ช่วงราคา': 'price',
      'ค่าเช่า/เดือน': 'price',
      'rental/month': 'price',
      'ค่าเช่า': 'price',
      
      'bedroom': 'bedroom', 
      'bedrooms': 'bedroom',
      'ห้องนอน': 'bedroom',
      'จำนวนห้องนอน': 'bedroom',
      'รูปแบบ': 'bedroom',
      'type': 'bedroom',
      
      'bathroom': 'bathroom',
      'bathrooms': 'bathroom',
      'ห้องน้ำ': 'bathroom',
      'จำนวนห้องน้ำ': 'bathroom',
      
      'area': 'area',
      'size': 'area',
      'พื้นที่': 'area',
      'ขนาด': 'area',
      'พื้นที่ใช้สอย': 'area',
      'usable area': 'area',
      
      'quota': 'quota',
      'โควต้า': 'quota',
      'สิทธิ': 'quota',
      
      'status': 'status',
      'สถานะ': 'status',
      'condition': 'status',
      
      'floor': 'floor',
      'ชั้น': 'floor',
      'level': 'floor',
      
      'view': 'view',
      'วิว': 'view',
      'ทิวทัศน์': 'view',
      
      'direction': 'direction',
      'ทิศ': 'direction',
      'ทิศทาง': 'direction',
      'orientation': 'direction',
      
      'publishedBy': 'publishedBy',
      'published_by': 'publishedBy',
      'ประกาศโดย': 'publishedBy',
      'โดย': 'publishedBy',
      'advertiser': 'publishedBy',
      'announce by': 'publishedBy',
      
      'postedDate': 'postedDate',
      'posted_date': 'postedDate',
      'ลงประกาศเมื่อ': 'postedDate',
      'วันที่ลงประกาศ': 'postedDate',
      'date_posted': 'postedDate',
      
      'distance': 'distance',
      'ระยะห่าง': 'distance'
    };

    const mappedFilters: string[] = [];
    
    result.filtersData.forEach(group => {
      const groupNameTh = group.group_name_th || '';
      const groupNameEn = group.group_name_en || '';
      const groupName = group.group_name || '';
      
      const mappedName = filterNameMap[groupNameTh.toLowerCase()] || 
                        filterNameMap[groupNameEn.toLowerCase()] ||
                        filterNameMap[groupName.toLowerCase()];
      
      if (mappedName && !mappedFilters.includes(mappedName)) {
        mappedFilters.push(mappedName);
      }
    });
    
    return mappedFilters;
  }, [result.filtersData]);

  return {
    ...result,
    filters
  };
}; 